(() => {
  const PICKER_SELECTOR = 'variant-picker.custom-variant';
  const TAB_SELECTOR = '.variant-tab[data-variant-tab]';
  const PANEL_SELECTOR = '.variant-panel[data-option-panel]';
  const OPT_CARD_SELECTOR = '.opt-card[data-option-value]';
  const TAB_VALUE_SELECTOR = '.variant-tab-value[data-tab-current]';

  const initializedPickers = new WeakSet();
  const activeTabTargets = new Map();
  const displaySelections = new Map();

  function getPickerKey(picker) {
    return [
      picker.dataset.sectionId || 'section',
      picker.dataset.productId || 'product',
      picker.dataset.blockId || 'block'
    ].join(':');
  }

  function getProductForm(picker) {
    return picker
      .closest('.shopify-section, dialog, product-card')
      ?.querySelector('product-form-component');
  }

  function getColorPropertyInput(picker) {
    return getProductForm(picker)?.querySelector('input[data-custom-color-property]');
  }

  function isColorOption(picker, optionIndex) {
    if (optionIndex === 'meta-color') return true;

    const tab = picker.querySelector(`${TAB_SELECTOR}[data-option-index="${optionIndex}"]`);
    return Boolean(tab?.classList.contains('variant-tab--color'));
  }

  function updateColorProperty(picker, optionIndex, optionValue) {
    const input = getColorPropertyInput(picker);
    if (!input || !isColorOption(picker, optionIndex)) return;

    input.value = optionValue || '';
  }

  // Get all tabs and panels within a picker
  function getElements(picker) {
    return {
      tabs: Array.from(picker.querySelectorAll(TAB_SELECTOR)),
      panels: Array.from(picker.querySelectorAll(PANEL_SELECTOR)),
      optCards: Array.from(picker.querySelectorAll(OPT_CARD_SELECTOR))
    };
  }

  // Update tab bar layout based on tab count
  function updateTabBarLayout(picker) {
    const tabBar = picker.querySelector('.variant-filter-bar');
    if (!tabBar) return;

    const tabs = tabBar.querySelectorAll('.variant-tab');
    const tabCount = tabs.length;

    // Set tab count attribute
    tabBar.setAttribute('data-tab-count', String(tabCount));

    // Check if tabs overflow container
    if (tabCount > 3) {
      tabBar.setAttribute('data-tabs-overflow', 'true');

      // Check if actually overflowing
      requestAnimationFrame(() => {
        const isOverflowing = tabBar.scrollWidth > tabBar.clientWidth;
        if (!isOverflowing) {
          tabBar.removeAttribute('data-tabs-overflow');
        }
        updateEdgeButtons(picker);
      });
    } else {
      tabBar.removeAttribute('data-tabs-overflow');
      updateEdgeButtons(picker);
    }

    setupTabScrollAffordances(picker);
  }

  // Show/hide the left/right scroll arrows based on current scroll position
  function updateEdgeButtons(picker) {
    const scroller = picker.querySelector('[data-variant-tabs-scroller]');
    if (!scroller) return;
    const bar = scroller.querySelector('.variant-filter-bar');
    const leftBtn = scroller.querySelector('.variant-filter-bar__edge--left');
    const rightBtn = scroller.querySelector('.variant-filter-bar__edge--right');
    if (!bar) return;

    const overflowing = bar.getAttribute('data-tabs-overflow') === 'true';
    if (!overflowing) {
      leftBtn && leftBtn.classList.remove('is-visible');
      rightBtn && rightBtn.classList.remove('is-visible');
      return;
    }

    const maxScroll = bar.scrollWidth - bar.clientWidth;
    const showLeft = bar.scrollLeft > 4;
    const showRight = bar.scrollLeft < maxScroll - 4;
    leftBtn && leftBtn.classList.toggle('is-visible', showLeft);
    rightBtn && rightBtn.classList.toggle('is-visible', showRight);
  }

  // Wire up arrow clicks, drag-to-scroll, and scroll-listener (idempotent)
  function setupTabScrollAffordances(picker) {
    const scroller = picker.querySelector('[data-variant-tabs-scroller]');
    if (!scroller || scroller.__affordancesInit) return;
    scroller.__affordancesInit = true;

    const bar = scroller.querySelector('.variant-filter-bar');
    if (!bar) return;

    // Arrow click → scroll by one tab width
    scroller.addEventListener('click', (e) => {
      const trigger = e.target.closest('[data-tabs-scroll]');
      if (!trigger) return;
      e.preventDefault();
      const dir = trigger.dataset.tabsScroll === 'left' ? -1 : 1;
      const sampleTab = bar.querySelector('.variant-tab');
      const step = sampleTab ? sampleTab.getBoundingClientRect().width + 8 : Math.round(bar.clientWidth * 0.6);
      bar.scrollBy({ left: dir * step, behavior: 'smooth' });
    });

    // Scroll listener → refresh arrow visibility
    bar.addEventListener('scroll', () => updateEdgeButtons(picker), { passive: true });

    // Mouse drag-to-scroll (desktop). Touch falls through to native overflow scroll.
    bar.classList.add('is-grabbable');
    let dragActive = false;
    let dragMoved = false;
    let startX = 0;
    let startScroll = 0;

    let capturedPointerId = null;

    bar.addEventListener('pointerdown', (e) => {
      if (e.pointerType !== 'mouse') return;
      if (e.target.closest('[data-tabs-scroll]')) return;
      dragActive = true;
      dragMoved = false;
      startX = e.clientX;
      startScroll = bar.scrollLeft;
      // Don't capture pointer yet — wait until we see real movement so plain
      // clicks on tabs still reach the tab and fire 'click'.
    });

    bar.addEventListener('pointermove', (e) => {
      if (!dragActive) return;
      const dx = e.clientX - startX;
      if (!dragMoved && Math.abs(dx) > 4) {
        dragMoved = true;
        bar.classList.add('is-grabbing');
        try {
          bar.setPointerCapture(e.pointerId);
          capturedPointerId = e.pointerId;
        } catch (_) {}
      }
      if (dragMoved) {
        bar.scrollLeft = startScroll - dx;
        e.preventDefault();
      }
    });

    const endDrag = () => {
      if (!dragActive) return;
      const wasDrag = dragMoved;
      dragActive = false;
      dragMoved = false;
      if (capturedPointerId !== null) {
        try { bar.releasePointerCapture(capturedPointerId); } catch (_) {}
        capturedPointerId = null;
      }
      if (wasDrag) {
        bar.classList.remove('is-grabbing');
        // Swallow the click that fires after a real drag so we don't switch tabs.
        const swallowOnce = (ev) => {
          ev.preventDefault();
          ev.stopPropagation();
          bar.removeEventListener('click', swallowOnce, true);
        };
        bar.addEventListener('click', swallowOnce, true);
        setTimeout(() => bar.removeEventListener('click', swallowOnce, true), 50);
      }
    };

    bar.addEventListener('pointerup', endDrag);
    bar.addEventListener('pointercancel', endDrag);
    // Also end on document-level mouseup, in case the pointer left the bar mid-drag
    document.addEventListener('mouseup', endDrag);
  }

  // Switch active tab and panel
  function switchTab(picker, targetId) {
    const { tabs, panels } = getElements(picker);
    activeTabTargets.set(getPickerKey(picker), targetId);

    // Update tabs
    tabs.forEach(tab => {
      const isActive = tab.dataset.tabTarget === targetId;
      tab.classList.toggle('is-active', isActive);
      tab.setAttribute('aria-selected', String(isActive));
      tab.setAttribute('tabindex', isActive ? '0' : '-1');
    });

    // Update panels
    panels.forEach(panel => {
      const isActive = panel.id === targetId;
      panel.classList.toggle('is-active', isActive);
      if (isActive) {
        panel.removeAttribute('hidden');
      } else {
        panel.setAttribute('hidden', '');
      }
    });

    // If the active tab is partially or fully off-screen, scroll it into view
    const activeTab = tabs.find(t => t.dataset.tabTarget === targetId);
    const bar = picker.querySelector('.variant-filter-bar[data-tabs-overflow="true"]');
    if (activeTab && bar) {
      const barRect = bar.getBoundingClientRect();
      const tabRect = activeTab.getBoundingClientRect();
      if (tabRect.left < barRect.left + 4 || tabRect.right > barRect.right - 4) {
        const target = activeTab.offsetLeft - (bar.clientWidth - activeTab.offsetWidth) / 2;
        bar.scrollTo({ left: Math.max(0, target), behavior: 'smooth' });
      }
    }
  }

  // Update the displayed value in tab button
  function updateTabValue(picker, optionIndex, value) {
    const tabValue = picker.querySelector(`${TAB_VALUE_SELECTOR}[data-option-index="${optionIndex}"]`);
    if (tabValue) {
      tabValue.textContent = value;
      tabValue.title = value;
    }
  }

  // Sizes that restrict certain storage options
  const RESTRICTED_SIZES = ['Single 3ft', 'Small Single 2FT6', 'Small Single 2ft6'];
  const DISABLED_STORAGE_OPTIONS = ['2 drawers end', '4 drawers'];

  function getSelectedSizeValue(picker) {
    const { panels, optCards } = getElements(picker);
    const sizePanel = panels.find(panel => {
      const name = (panel.dataset.optionName || '').toLowerCase();
      return name.includes('size');
    });
    if (!sizePanel) return null;
    const sizeIndex = sizePanel.dataset.optionPanel;
    const selectedCard = optCards.find(card =>
      card.dataset.optionIndex === sizeIndex && card.classList.contains('is-selected')
    );
    return selectedCard?.dataset.optionValue || null;
  }

  function updateStorageAvailability(picker) {
    const { panels, optCards } = getElements(picker);
    const selectedSize = getSelectedSizeValue(picker);
    const isRestricted = selectedSize && RESTRICTED_SIZES.some(s =>
      s.toLowerCase() === selectedSize.toLowerCase()
    );

    const storagePanel = panels.find(panel => {
      const name = (panel.dataset.optionName || '').toLowerCase();
      const cards = Array.from(panel.querySelectorAll(OPT_CARD_SELECTOR)).filter(
        (card) => card.dataset.displayOnly !== 'true'
      );
      const hasDrawerValues = cards.some((card) => /drawer/i.test(card.dataset.optionValue || ''));
      return cards.length > 1 && (name.includes('storage') || name.includes('headboard') || hasDrawerValues);
    });

    if (!storagePanel) return;

    const storageIndex = storagePanel.dataset.optionPanel;
    const storageCards = optCards.filter(card =>
      card.dataset.optionIndex === storageIndex && card.dataset.displayOnly !== 'true'
    );

    let needsReselect = false;

    storageCards.forEach(card => {
      const value = card.dataset.optionValue || '';
      const shouldDisable = isRestricted && DISABLED_STORAGE_OPTIONS.some(d =>
        d.toLowerCase() === value.toLowerCase()
      );

      card.classList.toggle('is-disabled', shouldDisable);
      card.setAttribute('aria-disabled', String(shouldDisable));

      if (shouldDisable && card.classList.contains('is-selected')) {
        card.classList.remove('is-selected');
        card.setAttribute('aria-pressed', 'false');
        needsReselect = true;
      }
    });

    if (needsReselect) {
      const firstAvailable = storageCards.find(card => !card.classList.contains('is-disabled'));
      if (firstAvailable) {
        selectVariant(picker, storageIndex, firstAvailable.dataset.optionValue);
      }
    }
  }

  function updatePriceDisplay(picker, selectedCard) {
    if (!selectedCard) return;

    const priceContainer = picker
      .closest('.shopify-section, dialog')
      ?.querySelector('product-price [ref="priceContainer"]');

    if (!priceContainer) return;

    const currentPrice = priceContainer.querySelector('.current-price');
    if (currentPrice && selectedCard.dataset.priceFormatted) {
      currentPrice.textContent = selectedCard.dataset.priceFormatted;
    }

    const compareWrap = priceContainer.querySelector('.save-compare');
    const oldPrice = priceContainer.querySelector('.old-price');
    const saveBadge = priceContainer.querySelector('.save-badge');
    const hasComparePrice = Boolean(selectedCard.dataset.comparePriceFormatted);

    if (oldPrice && saveBadge && compareWrap) {
      if (hasComparePrice) {
        oldPrice.textContent = selectedCard.dataset.comparePriceFormatted;
        saveBadge.textContent = `SAVE ${selectedCard.dataset.saveFormatted || ''}`.trim();
        compareWrap.hidden = false;
      } else {
        compareWrap.hidden = true;
      }
    }
  }


  // Handle variant selection
  function selectVariant(picker, optionIndex, optionValue) {
    const { optCards } = getElements(picker);
    const pickerKey = getPickerKey(picker);
    const activeTab = picker.querySelector(`${TAB_SELECTOR}.is-active`);

    if (activeTab?.dataset.tabTarget) {
      activeTabTargets.set(pickerKey, activeTab.dataset.tabTarget);
    }

    // Update selected state for options in the same group
    optCards.forEach(card => {
      if (card.dataset.optionIndex === optionIndex) {
        const isSelected = card.dataset.optionValue === optionValue;
        card.classList.toggle('is-selected', isSelected);
        card.setAttribute('aria-pressed', String(isSelected));
      }
    });

    // Update tab display value
    updateTabValue(picker, optionIndex, optionValue);
    updateColorProperty(picker, optionIndex, optionValue);
    updateStorageAvailability(picker);

    if (optionIndex === 'meta-color') {
      displaySelections.set(`${pickerKey}:${optionIndex}`, optionValue);
    }

    // Find variant ID and trigger update
    const selectedCard = optCards.find(card => (
      card.dataset.optionIndex === optionIndex &&
      card.dataset.optionValue === optionValue
    ));
    if (selectedCard) {
      if (selectedCard.dataset.displayOnly === 'true') {
        return;
      }

      updatePriceDisplay(picker, selectedCard);

      const variantId = selectedCard.dataset.variantId;
      const optionUrl = selectedCard.dataset.optionUrl;

      if (variantId) {
        // Dispatch custom event for theme to handle
        const event = new CustomEvent('variant:select', {
          bubbles: true,
          detail: {
            variantId: variantId,
            optionIndex: optionIndex,
            optionValue: optionValue,
            optionUrl: optionUrl
          }
        });
        picker.dispatchEvent(event);

        // Also trigger change on hidden radio if exists
        const radio = picker.querySelector(`input[type="radio"][data-option-value-id="${selectedCard.dataset.optionValueId}"]`);
        if (radio && !radio.checked) {
          radio.checked = true;
          radio.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }
    }
  }

  // Initialize picker
  function initPicker(picker) {
    if (!initializedPickers.has(picker)) {
      initializedPickers.add(picker);
    }

    // Set initial state from first active tab
    const { tabs, optCards } = getElements(picker);
    const pickerKey = getPickerKey(picker);
    const savedTargetId = activeTabTargets.get(pickerKey);
    const activeTab =
      tabs.find(t => t.dataset.tabTarget === savedTargetId) ||
      tabs.find(t => t.classList.contains('is-active')) ||
      tabs[0];

    if (activeTab) {
      const targetId = activeTab.dataset.tabTarget;
      if (targetId) {
        switchTab(picker, targetId);
      }
    }

    // Update initial values from selected options
    optCards.forEach(card => {
      if (card.classList.contains('is-selected')) {
        updateTabValue(picker, card.dataset.optionIndex, card.dataset.optionValue);
        if (isColorOption(picker, card.dataset.optionIndex)) {
          updateColorProperty(picker, card.dataset.optionIndex, card.dataset.optionValue);
        }
      }
    });

    const savedMetaColor = displaySelections.get(`${pickerKey}:meta-color`);
    if (savedMetaColor) {
      const savedCard = optCards.find(card => (
        card.dataset.optionIndex === 'meta-color' &&
        card.dataset.optionValue === savedMetaColor
      ));
      if (savedCard) {
        selectVariant(picker, 'meta-color', savedMetaColor);
      }
    }

    // Update tab bar layout
    updateTabBarLayout(picker);
    updateStorageAvailability(picker);
  }

  // Handle tab click
  function handleTabClick(event) {
    const tab = event.target.closest(TAB_SELECTOR);
    if (!tab) return;

    const picker = tab.closest(PICKER_SELECTOR);
    if (!picker) return;

    const targetId = tab.dataset.tabTarget;
    if (!targetId) return;

    event.preventDefault();
    switchTab(picker, targetId);
  }

  // Handle option card click
  function handleOptionClick(event) {
    const card = event.target.closest(OPT_CARD_SELECTOR);
    if (!card) return;

    const picker = card.closest(PICKER_SELECTOR);
    if (!picker) return;

    const optionIndex = card.dataset.optionIndex;
    const optionValue = card.dataset.optionValue;

    if (optionIndex == null || optionValue == null) return;
    if (card.classList.contains('is-selected')) return;
    if (card.classList.contains('is-disabled')) return;

    event.preventDefault();
    selectVariant(picker, optionIndex, optionValue);
  }

  // Handle keyboard navigation for tabs
  function handleKeydown(event) {
    const tab = event.target.closest(TAB_SELECTOR);
    if (!tab) return;

    const picker = tab.closest(PICKER_SELECTOR);
    if (!picker) return;

    const { tabs } = getElements(picker);
    const currentIndex = tabs.indexOf(tab);
    if (currentIndex === -1) return;

    let nextIndex = null;

    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        nextIndex = (currentIndex + 1) % tabs.length;
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        nextIndex = (currentIndex - 1 + tabs.length) % tabs.length;
        break;
      case 'Home':
        nextIndex = 0;
        break;
      case 'End':
        nextIndex = tabs.length - 1;
        break;
      default:
        return;
    }

    event.preventDefault();

    const nextTab = tabs[nextIndex];
    if (nextTab) {
      const targetId = nextTab.dataset.tabTarget;
      switchTab(picker, targetId);
      nextTab.focus();
    }
  }

  // Bind global events
  function bindEvents() {
    document.addEventListener('click', handleTabClick);
    document.addEventListener('click', handleOptionClick);
    document.addEventListener('keydown', handleKeydown);

    // Re-initialize on variant updates
    ['variant:change', 'variant:changed', 'product:variant-change', 'variant:update'].forEach(eventName => {
      document.addEventListener(eventName, () => {
        requestAnimationFrame(() => {
          document.querySelectorAll(PICKER_SELECTOR).forEach(picker => {
            initPicker(picker);
            updateTabBarLayout(picker);
          });
        });
      });
    });

    // Update layout on resize
    let resizeTimeout;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        document.querySelectorAll(PICKER_SELECTOR).forEach(updateTabBarLayout);
      }, 150);
    });
  }

  // Storefront API: fetch additional colors beyond the Liquid 50-item limit
  async function fetchExtraColors(picker) {
    const token = picker.dataset.storefrontToken;
    const handle = picker.dataset.productHandle;
    if (!token || !handle) return;

    const grids = picker.querySelectorAll('.opts-grid[data-color-count]');
    for (const grid of grids) {
      const colorCount = parseInt(grid.dataset.colorCount || '0', 10);
      if (colorCount < 50) continue; // all colors already rendered by Liquid

      const namespace = grid.dataset.colorListNamespace;
      const key = grid.dataset.colorListKey;
      const panelType = grid.dataset.colorPanel; // 'meta-color' or 'variant-color'

      let cursor = null;
      let hasMore = true;
      let fetched = 0;
      const skip = 50; // Liquid already rendered first 50

      while (hasMore) {
        const query = `
          query($handle: String!) {
            product(handle: $handle) {
              metafield(namespace: "${namespace}", key: "${key}") {
                references(first: 250${cursor ? `, after: "${cursor}"` : ''}) {
                  pageInfo { hasNextPage endCursor }
                  nodes {
                    ... on Metaobject {
                      fields {
                        key
                        value
                        reference {
                          ... on MediaImage {
                            image { url(transform: {maxWidth: 200}) }
                          }
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        `;

        try {
          const resp = await fetch('/api/2025-01/graphql.json', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-Shopify-Storefront-Access-Token': token,
            },
            body: JSON.stringify({ query, variables: { handle } }),
          });

          if (!resp.ok) break;
          const data = await resp.json();
          const refs = data?.data?.product?.metafield?.references;
          if (!refs) break;

          hasMore = refs.pageInfo.hasNextPage;
          cursor = refs.pageInfo.endCursor;

          const allNodes = refs.nodes || [];
          // Skip the first N nodes that Liquid already rendered
          const extraNodes = allNodes.slice(Math.max(0, skip - fetched));
          fetched += allNodes.length;

          for (const node of extraNodes) {
            const fields = node.fields || [];
            let colorName = '';
            let colorImgUrl = '';

            for (const field of fields) {
              if (field.key === 'name' || field.key === 'color_name') {
                colorName = field.value || '';
              }
              if (field.key === 'color_image' || field.key === 'image') {
                const ref = field.reference;
                if (ref?.image?.url) {
                  colorImgUrl = ref.image.url;
                } else if (field.value && (field.value.includes('cdn.shopify.com') || field.value.includes('//'))) {
                  colorImgUrl = field.value;
                }
              }
            }

            if (!colorName) continue;

            // Check if this color is already rendered
            const existing = grid.querySelector(`[data-option-value="${CSS.escape(colorName)}"]`);
            if (existing) continue;

            const card = document.createElement('button');
            card.type = 'button';
            card.className = 'opt-card opt-card--color';
            card.dataset.optionValue = colorName;
            card.setAttribute('aria-pressed', 'false');
            card.setAttribute('aria-label', colorName);

            if (panelType === 'meta-color') {
              card.dataset.optionIndex = 'meta-color';
              card.dataset.displayOnly = 'true';
            } else {
              // Find matching product option value for variant selection
              const optionIndex = grid.dataset.optionPanel;
              card.dataset.optionIndex = optionIndex;
              // Try to match with a hidden radio input for variant selection
              const radio = picker.querySelector(
                `input[type="radio"][value="${CSS.escape(colorName)}"]`
              );
              if (radio) {
                card.dataset.optionValueId = radio.dataset.optionValueId || '';
                card.dataset.variantId = radio.dataset.variantId || '';
                const url = radio.dataset.connectedProductUrl;
                if (url) card.dataset.optionUrl = `${url}?option_values=${radio.dataset.optionValueId || ''}`;
              }
            }

            card.innerHTML = `
              <span class="opt-card-check" aria-hidden="true">
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M2 6L5 9L10 3" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
              </span>
              <span class="opt-card-swatch${colorImgUrl ? ' has-image' : ''}">
                ${colorImgUrl ? `<img src="${colorImgUrl}" alt="${colorName}" width="200" height="200" loading="lazy" onerror="this.style.display='none'; this.nextElementSibling.style.display='flex';">` : ''}
                <span class="opt-card-icon"${colorImgUrl ? ' style="display:none;"' : ''}></span>
              </span>
              <span class="opt-card-name">${colorName}</span>
            `;

            grid.appendChild(card);
          }

          // Safety: don't fetch more than 500 total
          if (fetched >= 500) break;
        } catch (err) {
          console.warn('Storefront API color fetch failed:', err);
          break;
        }
      }

      // Update color count attribute
      const totalRendered = grid.querySelectorAll('.opt-card').length;
      grid.dataset.colorCount = String(totalRendered);
    }
  }

  // Initialize
  function init() {
    document.querySelectorAll(PICKER_SELECTOR).forEach((picker) => {
      initPicker(picker);
      fetchExtraColors(picker);
    });
    bindEvents();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
